/**
 * Resume Search — full-text keyword search across resumes.  GET /resumes/search?q=
 * Ranked candidate cards with a highlighted snippet and a relative match indicator.
 * Note: PDFs uploaded in Phase 1 aren't text-indexed yet, so they won't appear here.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Briefcase, Clock, Info } from 'lucide-react';
import { Button, Skeleton } from '../../components/ui';
import { Card } from '../../components/ui/Card';
import { Avatar, EmptyState, ErrorState } from '../../features/recruitment';
import { useResumeSearch } from '../../features/recruitment/hooks/useResumeSearch';
import { formatExperience, formatDate } from '../../features/recruitment/utils/format';
import type { ResumeSearchResultItem } from '../../services/recruitmentApi';

const SUGGESTIONS = ['commercial kitchen equipment', 'forklift license', 'warehouse supervisor', 'food safety'];

/** Split text around query terms and wrap matches in <mark>. */
function highlight(text: string, query: string): React.ReactNode {
	const terms = query
		.split(/\s+/)
		.map(t => t.trim())
		.filter(t => t.length > 1)
		.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
	if (terms.length === 0) return text;
	const re = new RegExp(`(${terms.join('|')})`, 'gi');
	const parts = text.split(re);
	return parts.map((part, i) =>
		re.test(part) ? (
			<mark key={i} className='rounded bg-yellow-100 px-0.5 text-gray-900'>
				{part}
			</mark>
		) : (
			<React.Fragment key={i}>{part}</React.Fragment>
		)
	);
}

export const ResumeSearch: React.FC = () => {
	const navigate = useNavigate();
	const [input, setInput] = useState('');
	const [submitted, setSubmitted] = useState('');

	const { data, isLoading, isError, refetch, isFetching } = useResumeSearch(submitted);

	const results = useMemo(() => data?.results ?? [], [data]);
	const maxScore = useMemo(() => results.reduce((m, r) => Math.max(m, r.score || 0), 0), [results]);

	const runSearch = (q?: string) => {
		const value = (q ?? input).trim();
		setInput(value);
		setSubmitted(value);
	};

	const matchPct = (score: number) => (maxScore > 0 ? Math.round((score / maxScore) * 100) : 0);

	return (
		<div className='min-h-screen bg-gray-50 p-6'>
			<div className='mx-auto max-w-4xl'>
				<h1 className='text-2xl font-semibold text-gray-900'>Resume Search</h1>
				<p className='mt-1 text-sm text-gray-500'>Search across all indexed resumes by skill, tool or keyword.</p>

				{/* Search bar */}
				<div className='mt-5'>
					<div className='flex gap-2'>
						<div className='relative flex-1'>
							<Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
							<input
								type='text'
								value={input}
								onChange={e => setInput(e.target.value)}
								onKeyDown={e => e.key === 'Enter' && runSearch()}
								placeholder="Search all resumes — e.g. 'commercial kitchen equipment'"
								className='h-11 w-full rounded-xl border border-gray-300 pl-10 pr-3 text-sm text-gray-900 outline-none focus:border-primary focus:ring-1 focus:ring-primary'
							/>
						</div>
						<Button variant='primary' size='lg' onClick={() => runSearch()} loading={isFetching} disabled={!input.trim()}>
							Search
						</Button>
					</div>

					{/* Suggestion chips */}
					<div className='mt-3 flex flex-wrap gap-2'>
						{SUGGESTIONS.map(s => (
							<button
								key={s}
								type='button'
								onClick={() => runSearch(s)}
								className='rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 hover:border-primary/40 hover:text-gray-900'
							>
								{s}
							</button>
						))}
					</div>

					<p className='mt-3 flex items-center gap-1 text-xs text-gray-400'>
						<Info className='h-3 w-3' /> Searches indexed resumes. Recently uploaded PDFs are not text-indexed yet and will not appear.
					</p>
				</div>

				{/* Results */}
				<div className='mt-6'>
					{!submitted ? (
						<EmptyState
							title='Search resumes'
							message='Type a skill or keyword above to find matching candidates, ranked by relevance.'
						/>
					) : isLoading ? (
						<div className='space-y-3'>
							{[0, 1, 2].map(i => (
								<Skeleton key={i} className='h-28 w-full' />
							))}
						</div>
					) : isError ? (
						<ErrorState message='Resume search failed. Please try again.' onRetry={() => refetch()} />
					) : results.length === 0 ? (
						<EmptyState title={`No resumes match "${submitted}"`} message='Try a different keyword or a broader term.' />
					) : (
						<>
							<p className='mb-3 text-sm text-gray-500'>
								{data?.count ?? results.length} result{(data?.count ?? results.length) === 1 ? '' : 's'} for{' '}
								<span className='font-medium text-gray-700'>&ldquo;{data?.query ?? submitted}&rdquo;</span>
							</p>
							<div className='space-y-3'>
								{results.map((r: ResumeSearchResultItem) => (
									<Card
										key={`${r.candidate_id}-${r.filename ?? ''}`}
										className='cursor-pointer transition hover:border-primary/40 hover:shadow-sm'
										onClick={() => navigate(`/recruitment/candidates/${r.candidate_id}`)}
									>
										<div className='flex items-start gap-3'>
											<Avatar name={r.full_name} />
											<div className='min-w-0 flex-1'>
												<div className='flex items-start justify-between gap-3'>
													<div className='min-w-0'>
														<p className='truncate text-sm font-semibold text-gray-900'>{r.full_name}</p>
														{(r.current_role || r.current_company) && (
															<p className='mt-0.5 flex items-center gap-1 truncate text-xs text-gray-500'>
																<Briefcase className='h-3 w-3' /> {[r.current_role, r.current_company].filter(Boolean).join(' · ')}
															</p>
														)}
													</div>
													<span className='shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-700'>
														{matchPct(r.score)}% match
													</span>
												</div>

												{r.snippet && (
													<p className='mt-2 line-clamp-3 text-sm text-gray-600'>…{highlight(r.snippet, submitted)}…</p>
												)}

												<div className='mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400'>
													<span>{formatExperience(r.total_experience)}</span>
													{r.location && (
														<span className='flex items-center gap-1'>
															<MapPin className='h-3 w-3' /> {r.location}
														</span>
													)}
													<span className='flex items-center gap-1'>
														<Clock className='h-3 w-3' /> Resume updated {formatDate(r.resume_updated_at)}
													</span>
													{r.filename && <span className='truncate'>{r.filename}</span>}
												</div>
											</div>
										</div>
									</Card>
								))}
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};
